import { Col, Grid, Input, message, Row, Select, Space, Tag } from "antd";
import { SubscriptionLevel } from "../../Types/SubscriptionLevel";
import { ReactElement, useState } from "react";
import Table, { ColumnsType } from "antd/lib/table";
import { CheckCircleOutlined, EditOutlined, HourglassOutlined, QuestionCircleOutlined, StarFilled, StopOutlined, UserOutlined } from "@ant-design/icons";
import { formatDateRangeString, formatDateRangeWithDateString } from "../../Helpers/DateHelpers";
import moment from "moment";
import { CloseEventModal } from "./CloseEventModal";
import { useOfferSignUpForChosenTime } from "./ClosePollModal";
import { EventState } from "../../Pages/Event/EventState";
import DeleteReplyIcon from "./DeleteReplyIcon";
import { useNavigate } from "react-router-dom";
import { TextService } from "../../Services/TextService";
import { getCurrentLanguage } from "../../Helpers/RouteHelper";
import { isOnlyOnWaitlist } from "../../Helpers/WaitlistHelper";
import { EventType } from "../../Types/EventType";
import { isOnlyComing } from "../../Types/AnswerOptions";
import { compareNames, matchesReplySearch } from "../../Helpers/ReplySearchHelper";

// The order the answers are listed and sorted in: place, maybe, waitlist, no.
const ANSWER_ORDER = [0, 1, 3, 2];
const ALL = "all";

/**
 * The replies of an event. A poll shows the votes for each time, marks the time with the most votes and
 * is closed by choosing the final time. A sign-up shows who has a place: as a plain list when the event
 * has one time, and with a column for each time when it has several.
 */
export const ReplyTable = (props: any) => {
    const [open, setOpen] = useState(false);
    // The time chosen as final when a poll is closed.
    const [chosenTime, setChosenTime] = useState(undefined as any);
    // Search and filter, so a long list can be narrowed down to the people looked for.
    const [searchText, setSearchText] = useState("");
    const [answerFilter, setAnswerFilter] = useState(ALL as string);
    const [timeFilter, setTimeFilter] = useState(ALL as string);
    const navigation = useNavigate();
    const isMobile = !Grid.useBreakpoint().md;
    const GetText = TextService.GetText;
    interface DataType {
        key: string;
        customerName: string;
        customerEmail: string;
        customerPhone?: string;
        isMember?: boolean;
        customerAnswer: string;
        authCode: string;
        id: number;
        times: any[];
        tags: string[];
    }

    const isPoll = props.event.eventType === EventType.Poll;
    const times: any[] = props.event.times ?? [];
    const isSingleTimeSignUp = !isPoll && times.length === 1;
    // A "no" on a sign-up with only "Kommer" is someone who cancelled.
    const noText = isOnlyComing(props.event) ? "Event_Cancelled" : "Event_NotComing";

    const columns: ColumnsType<DataType> = [
        {
            title: () => { return <><UserOutlined /> {GetText("ReplyTable_Headline")}</> },
            dataIndex: 'customerName',
            key: 'customerName',
            sorter: (a, b) => compareNames(a.customerName, b.customerName),
            // With a column for each time the name stays in place while the times are scrolled sideways on a phone.
            fixed: isSingleTimeSignUp ? undefined : 'left',
            width: isSingleTimeSignUp || !isMobile ? undefined : 160,
            render: (_, reply) => (
                <Row wrap={false} align="middle" gutter={12}>
                    <Col flex="auto">
                        <b>{reply.customerName}</b>
                        {/* Most participants are members, so only the guests are marked. */}
                        {!reply.isMember && <Tag className="reply-guest-tag" color="orange" bordered={false}>{GetText("ReplyTable_Guest")}</Tag>}
                        {reply.customerEmail ? <div className="reply-guest-sub">{reply.customerEmail}</div> : ""}
                        {reply.customerPhone ? <div className="reply-guest-sub">{reply.customerPhone}</div> : ""}
                        {reply.customerAnswer ? <div className="reply-guest-sub"><i>{reply.customerAnswer}</i></div> : ""}
                    </Col>
                    <Col flex="none">
                        <Space size="middle">
                            <EditOutlined onClick={e => onEditClick(reply.authCode)} />
                            <DeleteReplyIcon onReplyDelete={onReplyDelete} reply={reply} />
                        </Space>
                    </Col>
                </Row>
            ),
        }
    ];

    const onReplyDelete = () => {
        props.onReplyDelete();
    };

    const onEditClick = (authCode: string) => {

        if (props.user.subscriptionLevel == SubscriptionLevel.Free) {
            message.warning(GetText("ReplyTable_EditReplyOnlyForPremium"));
            return;
        }

        navigation("/" + getCurrentLanguage() + "/Event/" + props.event.eventUniqueId + "/Reply/" + authCode)
    };

    const isAlreadyClosed = () => {
        if (props.event.eventState === EventState.Closed) {
            message.warning(GetText("ReplyTable_AlreadyClosed"));
            return true;
        }

        return false;
    };

    // A poll is closed by choosing the final time.
    const onTimeSelect = (time: any) => {
        if (isAlreadyClosed()) {
            return;
        }

        setChosenTime(time);
        setOpen(true);
    }

    const offerSignUpForChosenTime = useOfferSignUpForChosenTime(props.event);

    const onPollClosed = (time: any) => {
        setOpen(false);
        props.onClosePoll();
        offerSignUpForChosenTime(time);
    };

    // The answer comes from the row's own reply. Replies can lack e-mail, so e-mail can't be used to find it.
    const getAnswerType = (reply: any, timeId: string): number | undefined => {
        return reply.replyTime?.find((replyTime: any) => replyTime.timeId == timeId)?.customerAnswerType;
    }

    const getAnswer = (reply: any, timeId: string): ReactElement => {
        switch (getAnswerType(reply, timeId)) {
            case 0:
                return <span className='accepted'><CheckCircleOutlined /> </span>
            case 1:
                return <span className='tentative'><QuestionCircleOutlined /></span>
            case 2:
                return <span className='rejected'><StopOutlined /></span>
            case 3:
                return <span className='tentative' title={GetText("ReplyTimeForm_Waitlisted")}><HourglassOutlined /></span>
            default:
                // A vote that is missing has always been shown like this in a poll.
                return isPoll ? <CheckCircleOutlined /> : <></>;
        }
    }

    // With one time there is nothing to compare, so the answer is written out.
    const getStatus = (reply: any, timeId: string): ReactElement => {
        switch (getAnswerType(reply, timeId)) {
            case 0:
                return <span className='accepted'><CheckCircleOutlined /> {GetText("ReplyTable_StatusSignedUp")}</span>
            case 1:
                return <span className='tentative'><QuestionCircleOutlined /> {GetText("ReplyTimeForm_Tentative")}</span>
            case 2:
                return <span className='rejected'><StopOutlined /> {GetText(isPoll ? "ReplyTimeForm_NoButtonText" : noText)}</span>
            case 3:
                return <span className='tentative'><HourglassOutlined /> {GetText("ReplyTimeForm_Waitlisted")}</span>
            default:
                return <></>;
        }
    }

    // A missing answer is sorted last.
    const getAnswerRank = (reply: any, timeId: string): number => {
        const rank = ANSWER_ORDER.indexOf(getAnswerType(reply, timeId) ?? -1);
        return rank < 0 ? ANSWER_ORDER.length : rank;
    }

    const getAnswerLabel = (answerType: number): string => {
        switch (answerType) {
            case 0:
                return GetText(isPoll ? "Common_Yes" : "ReplyTable_StatusSignedUp");
            case 1:
                return GetText("ReplyTimeForm_Tentative");
            case 3:
                return GetText("ReplyTimeForm_Waitlisted");
            default:
                return GetText(isPoll ? "Common_No" : noText);
        }
    }

    const replies: any[] = (props.event.timeAnswers ?? []).filter((reply: any) => !isOnlyOnWaitlist(reply));

    // Only the answers somebody has given can be chosen, so a sign-up doesn't offer "maybe".
    const answerOptions = ANSWER_ORDER
        .filter(answerType => replies.some(reply => reply.replyTime?.some((replyTime: any) => replyTime.customerAnswerType === answerType)))
        .map(answerType => ({ value: answerType.toString(), label: getAnswerLabel(answerType) }));

    // With all times chosen, a person matches when the answer is given for some of the times.
    const matchesAnswerFilter = (reply: any): boolean => {
        if (answerFilter === ALL) {
            return true;
        }

        return (reply.replyTime ?? []).some((replyTime: any) =>
            replyTime.customerAnswerType?.toString() === answerFilter && (timeFilter === ALL || replyTime.timeId?.toString() === timeFilter));
    }

    const filteredReplies = replies.filter(reply => matchesReplySearch(reply, searchText) && matchesAnswerFilter(reply));
    const isFiltered = filteredReplies.length !== replies.length;

    if (isSingleTimeSignUp) {
        columns.push({
            title: GetText("ReplyTable_Status"),
            key: 'status',
            sorter: (a, b) => getAnswerRank(a, times[0].id) - getAnswerRank(b, times[0].id),
            render: (_, reply) => getStatus(reply, times[0].id),
        });
    }
    else {
        times.forEach(function (time: any) {
            columns.push({
                title: () => {
                    return <div onClick={isPoll ? () => onTimeSelect(time) : undefined} className="date-header" style={isPoll ? undefined : { cursor: "default" }}>
                        <div className="date-title">{isPoll && time.hasMostYesCount ? <span className="date-hasMostYesCount"><StarFilled /> </span> : ""}{moment(time.start).format("ddd D MMM")}</div>
                        <div className="date-sub">
                            {!time.isFullDay ? <>{formatDateRangeString(time.start, time.end, time.isFullDay, getCurrentLanguage() == "se")} &middot; </> : ""}
                            <UserOutlined /> {time.yesCount}{isPoll ? "" : "/" + props.event.eventParticipants}
                        </div>
                    </div>;
                },
                dataIndex: 'customerName',
                key: 'time-' + time.id,
                // In a poll a click on the time closes the poll, so it can't also sort.
                sorter: isPoll ? undefined : (a, b) => getAnswerRank(a, time.id) - getAnswerRank(b, time.id),
                render: (_, reply) => (
                    <div className="reply-answer"><b>{getAnswer(reply, time.id)}</b></div>
                ),
            })
        });
    }

    return <>
        {/* A sign-up is closed from the status at the top of the page. */}
        {isPoll ? <CloseEventModal event={props.event} open={open} chosenTime={chosenTime} onCancel={() => setOpen(false)} onClosed={onPollClosed} /> : null}

        {isPoll ? <div style={{ textAlign: "left", marginBottom: "10px" }}><i>{GetText("ReplyTable_EventCloseHelpText")}</i></div> :
            isSingleTimeSignUp ?
                <div style={{ textAlign: "left", marginBottom: "10px" }}>
                    <b>{formatDateRangeWithDateString(times[0].start, times[0].end, times[0].isFullDay, getCurrentLanguage() == "se")}</b>
                    <span> &middot; {times[0].yesCount} {GetText("ReplyTable_OfPlaces")} {props.event.eventParticipants} {GetText("ReplyTable_Places")}</span>
                </div> : null}

        <Row gutter={[8, 8]} style={{ marginBottom: "10px" }}>
            <Col flex="auto">
                <Input.Search allowClear placeholder={GetText("ReplyTable_SearchPlaceholder")} value={searchText} onChange={(e) => setSearchText(e.target.value)} />
            </Col>
            <Col flex="none">
                <Select style={{ minWidth: 160 }} value={answerFilter} onChange={setAnswerFilter}
                    options={[{ value: ALL, label: GetText("ReplyTable_AllAnswers") }, ...answerOptions]} />
            </Col>
            {/* The time only matters for which answer is looked for. */}
            {times.length > 1 && answerFilter !== ALL ?
                <Col flex="none">
                    <Select style={{ minWidth: 180 }} value={timeFilter} onChange={setTimeFilter}
                        options={[{ value: ALL, label: GetText("ReplyTable_AllTimes") }, ...times.map((time: any) => ({
                            value: time.id.toString(),
                            label: moment(time.start).format("ddd D MMM") + (time.isFullDay ? "" : " " + formatDateRangeString(time.start, time.end, time.isFullDay, getCurrentLanguage() == "se"))
                        }))]} />
                </Col> : null}
        </Row>

        <Table columns={columns} scroll={isSingleTimeSignUp ? undefined : { x: 'max-content' }} className="reply-table" rowKey="id" dataSource={filteredReplies}
            locale={{ emptyText: GetText("ReplyTable_NoMatches") }}
            pagination={{
                defaultPageSize: 20,
                showSizeChanger: true,
                locale: { items_per_page: "/ " + GetText("Common_Page") },
                hideOnSinglePage: replies.length <= 20,
                showTotal: (total) => isFiltered ? `${total} ${GetText("Common_Of")} ${replies.length} ${GetText("ReplyTable_Persons")}` : `${total} ${GetText("ReplyTable_Persons")}`
            }} />
    </>
}
